import { useState } from 'react';
import { fetchGeographicAddressById, searchGeographicAddress } from '../../../common/fetch.js';
import {
  type ApartmentOption,
  findGeoAddressForMatch,
  flattenApartments,
  mapToStreetResults,
  resolvePostOffice,
} from '../utils/geographicAddressUtilsCoCo';
import type { GeographicAddress } from '../../../generated/api/geographicAddress.js';
import type { AddressSearchResult } from '../../AddressSearch/AddressSearch.js';

export interface UseAddressSearchOptions {
  postalCode: string;
  onDisplayTextChange: (displayText: string) => void;
}

export interface UseAddressSearchResult {
  apartments: ApartmentOption[];
  addressSearchResult: AddressSearchResult | undefined;
  matchedAddressId: string | undefined;
  postOffice: string;
  resetApartments: () => void;
  onSearchAddress: (searchText: string, zipCode: string) => Promise<void>;
  onSelectStreet: (addressId: string, streetLabel: string) => Promise<void>;
  onSelectApartment: (apt: ApartmentOption) => void;
  getDropdownItems: () => { key: string; displayText: string }[];
}

export const useAddressSearch = ({
  postalCode,
  onDisplayTextChange,
}: UseAddressSearchOptions): UseAddressSearchResult => {
  const [apartments, setApartments] = useState<ApartmentOption[]>([]);
  const [geographicSearchResults, setGeographicSearchResults] = useState<GeographicAddress[]>([]);
  const [addressSearchResult, setAddressSearchResult] = useState<AddressSearchResult | undefined>();

  const resetApartments = () => setApartments([]);

  const selectAddress = (addressId: string, addressText: string) => {
    onDisplayTextChange(addressText);
    setAddressSearchResult({
      matchedAddresses: [{ addressId, addressText }],
      match: { addressId, addressText },
      postalCode,
    });
    setApartments([]);
  };

  const onSearchAddress = async (searchText: string, zipCode: string) => {
    setApartments([]);
    try {
      const response = await searchGeographicAddress(searchText, zipCode);
      setGeographicSearchResults(response);
      setAddressSearchResult(mapToStreetResults(response, zipCode));
    } catch {
      setGeographicSearchResults([]);
      setAddressSearchResult(undefined);
    }
  };

  const onSelectStreet = async (addressId: string, streetLabel: string) => {
    setApartments([]);
    try {
      const apartmentOptions = flattenApartments(await fetchGeographicAddressById(addressId));
      if (apartmentOptions.length > 0) {
        setApartments(apartmentOptions);
      } else {
        selectAddress(addressId, streetLabel);
      }
    } catch {
      // ignore: apartments already cleared above
    }
  };

  const onSelectApartment = (apt: ApartmentOption) => selectAddress(apt.addressId, apt.displayText);

  const getDropdownItems = (): { key: string; displayText: string }[] => {
    if (apartments.length > 0) {
      return apartments.map(({ addressId, displayText }) => ({ key: addressId, displayText }));
    }
    const matchText = addressSearchResult?.match?.addressText;
    return (addressSearchResult?.matchedAddresses ?? [])
      .filter(({ addressText }) => addressText !== matchText)
      .map(({ addressId, addressText }) => ({ key: addressId, displayText: addressText }));
  };

  const matchedAddressId = addressSearchResult?.match?.addressId;
  const postOffice = resolvePostOffice(findGeoAddressForMatch(geographicSearchResults, matchedAddressId, postalCode));

  return {
    apartments,
    addressSearchResult,
    matchedAddressId,
    postOffice,
    resetApartments,
    onSearchAddress,
    onSelectStreet,
    onSelectApartment,
    getDropdownItems,
  };
};
